import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PendingBranchClear } from '@/hooks/submission_forms/useBranchClearGuard';

const MAX_LISTED = 5;

interface BranchClearConfirmDialogProps {
  pending: PendingBranchClear | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Warns the user that changing a gating answer will permanently clear their
 * responses to questions that will no longer apply. Switching the gating
 * answer back re-reveals those questions, but the cleared responses are gone.
 */
export const BranchClearConfirmDialog = ({
  pending,
  onConfirm,
  onCancel,
}: BranchClearConfirmDialogProps) => {
  const affected = pending?.affectedQuestions ?? [];
  const listed = affected.slice(0, MAX_LISTED);
  const remaining = affected.length - listed.length;

  return (
    <Dialog open={pending !== null} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Clear answers that no longer apply?
          </DialogTitle>
          <DialogDescription>
            Changing this answer will permanently clear{' '}
            {affected.length === 1
              ? 'your response to 1 question that'
              : `your responses to ${affected.length} questions that`}{' '}
            will no longer apply to your submission. You can still switch back
            to this answer, but the cleared{' '}
            {affected.length === 1 ? 'response' : 'responses'} won&apos;t be
            saved.
          </DialogDescription>
        </DialogHeader>

        {listed.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground max-h-48 overflow-y-auto">
            {listed.map(question => (
              <li key={question.slug}>{question.prompt}</li>
            ))}
            {remaining > 0 && (
              <li className="list-none pl-0 italic">…and {remaining} more</li>
            )}
          </ul>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            Keep my answer
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm}>
            Change answer and clear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
